import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PAYMENT, RIDE_TYPES, STATUS_LABEL } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { RideAdminCard } from "@/pages/admin/RideAdminCard";
import { Input } from "@/components/ui/input";

const Sel = ({ value, onChange, options, all, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid={testid}>
    <option value="">{all}</option>
    {options.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
  </select>
);

export default function AdminRideHistory() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ range: params.get("range") || "", status: "", rider: "", driver: "", ride_type: "", payment_status: "",
    pickup: "", dropoff: "", min_fare: "", max_fare: "", start: "", end: "" });
  const set = (k) => (v) => setF({ ...f, [k]: typeof v === "string" ? v : v.target.value });
  const query = Object.fromEntries(Object.entries(f).filter(([k, v]) => v !== "" && !(["start", "end"].includes(k) && f.range !== "custom"))
    .map(([k, v]) => [k, ["start", "end"].includes(k) ? new Date(v).toISOString() : v]).filter(([k]) => !(k === "range" && f.range === "custom")));
  const { data } = useQuery({ queryKey: ["adminRides", query], queryFn: () => api.get("/admin/rides", { params: query }).then((r) => r.data), refetchInterval: 10000 });
  return (
    <div className="fade-up" data-testid="admin-rides">
      <PageHead title="Ride history" sub={`${data?.length ?? 0} rides`} />
      <div className="mb-5 flex flex-wrap gap-2" data-testid="ride-filters">
        <Sel value={f.range} onChange={set("range")} all="Any date" testid="filter-range" options={[["today", "Today"], ["week", "This week"], ["month", "This month"], ["custom", "Custom range"]]} />
        {f.range === "custom" && <>
          <Input type="date" value={f.start} onChange={set("start")} className="h-9 w-40 [color-scheme:dark]" data-testid="filter-start" />
          <Input type="date" value={f.end} onChange={set("end")} className="h-9 w-40 [color-scheme:dark]" data-testid="filter-end" />
        </>}
        <Sel value={f.status} onChange={set("status")} all="Any status" testid="filter-status" options={Object.entries(STATUS_LABEL)} />
        <Sel value={f.ride_type} onChange={set("ride_type")} all="Any type" testid="filter-ride-type" options={Object.entries(RIDE_TYPES)} />
        <Sel value={f.payment_status} onChange={set("payment_status")} all="Any payment" testid="filter-payment" options={PAYMENT.map((p) => [p, p.toUpperCase()])} />
        {[["rider", "Rider"], ["driver", "Driver"], ["pickup", "Pickup"], ["dropoff", "Destination"]].map(([k, l]) => (
          <Input key={k} placeholder={l} value={f[k]} onChange={set(k)} className="h-9 w-36" data-testid={`filter-${k}`} />
        ))}
        <Input type="number" placeholder="Min $" value={f.min_fare} onChange={set("min_fare")} className="h-9 w-24" data-testid="filter-min-fare" />
        <Input type="number" placeholder="Max $" value={f.max_fare} onChange={set("max_fare")} className="h-9 w-24" data-testid="filter-max-fare" />
      </div>
      {!data?.length && <p className="text-sm text-muted-foreground">No rides match.</p>}
      <div className="grid gap-3 lg:grid-cols-2">{data?.map((r) => <RideAdminCard key={r.id} ride={r} />)}</div>
    </div>
  );
}
